import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicLayout, SectionHeading } from "../components/PublicLayout";
import api from "../lib/api";
import { Ticket, BookOpen, MonitorPlay, Timer, Users, LayoutDashboard } from "lucide-react";

const FEATURES = [
  { icon: Ticket, title: "Full ticketing", desc: "Priorities P1–P4, statuses, categories, assignment, comments and private work notes — the complete incident workflow." },
  { icon: Timer, title: "SLA tracking", desc: "Automatic SLA due dates per priority with breach visibility on your dashboard." },
  { icon: BookOpen, title: "Knowledge base", desc: "Searchable articles your agents write once and your users find instantly." },
  { icon: MonitorPlay, title: "Remote support", desc: "Built-in browser screen sharing — no installs — plus one-click links to TeamViewer or AnyDesk." },
  { icon: Users, title: "Team workspace", desc: "Invite agents and end-users to your own private company workspace." },
  { icon: LayoutDashboard, title: "Live dashboard", desc: "Open tickets, unassigned queue, SLA breaches and resolution stats at a glance." },
];

export default function HelpdeskLanding() {
  const [pricing, setPricing] = useState(null);
  useEffect(() => { api.get("/helpdesk/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);
  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="helpdesk-landing">
        <p className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold rounded-full px-4 py-1.5 mb-5">GloriDesk — Helpdesk Platform</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight font-heading max-w-3xl leading-[1.05]">
          An enterprise-grade helpdesk, <span className="text-teal-700">without the enterprise price</span>
        </h1>
        <p className="text-[#52525B] mt-6 max-w-2xl leading-relaxed">
          Tickets, SLAs, knowledge base and built-in remote screen sharing — everything your support team needs, live in minutes.
        </p>
        <div className="mt-8 flex items-center gap-3">
          <Link to="/helpdesk/signup" data-testid="helpdesk-cta-signup"
            className="px-7 py-3 rounded-full text-white bg-[#0A0A0A] hover:bg-[#27272A] text-sm font-medium transition-colors">
            Start {pricing?.trial_days || 14}-day free trial
          </Link>
          <Link to="/login" className="px-6 py-3 rounded-full border border-[#E4E4E7] text-sm font-medium hover:bg-[#F5F5F7] transition-colors">Log in</Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-20">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-white border border-[#E4E4E7] rounded-3xl p-7 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <f.icon size={22} className="text-[#0A0A0A]" />
              <h3 className="font-semibold font-heading mt-4">{f.title}</h3>
              <p className="text-sm text-[#52525B] mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-20 bg-[#0B1F33] text-white rounded-3xl p-10 sm:p-14 flex flex-wrap items-center gap-10 justify-between">
          <div>
            <h2 className="text-lg md:text-lg font-bold font-heading">Simple pricing</h2>
            <p className="text-white/60 mt-2 max-w-md text-sm leading-relaxed">One flat rate for your whole company — unlimited agents, users, tickets and articles.</p>
          </div>
          <div className="text-right">
            <p className="text-5xl font-bold font-heading" data-testid="helpdesk-price">${pricing ? pricing.monthly_price.toFixed(0) : "99"}<span className="text-lg text-white/50 font-normal">/mo</span></p>
            <Link to="/helpdesk/signup" data-testid="helpdesk-pricing-cta"
              className="inline-block mt-4 px-6 py-2.5 rounded-full bg-white text-[#0A0A0A] text-sm font-semibold hover:bg-neutral-200 transition-colors">
              Try it free
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
